/**
 * Замена `flutter_easyloading`.
 *
 * В приложении от пакета использовались ровно два вызова — `EasyLoading.show()`
 * и `EasyLoading.dismiss()`, — плюс настройка из `main.dart`:
 *
 *     EasyLoading.instance.userInteractions = false;
 *
 * то есть пока висит индикатор, нажатия до интерфейса не доходят. Поэтому
 * `LoadingOverlayHost` рисует полноэкранный слой, который забирает все
 * нажатия себе, а не просто крутит спиннер поверх экрана.
 *
 * Отличие от пакета: здесь считается число незакрытых `show()`.
 * `flutter_easyloading` прятал индикатор любым `dismiss()`, и при вложенных
 * операциях («показать → показать → закрыть») экран мигал. Счётчик убирает
 * мигание и не ломает принятый в коде порядок `dismiss(); show(); … finally
 * dismiss();` — он сводится к нулю так же, как раньше.
 */
export class LoadingOverlay {
  private constructor() {}

  static readonly instance = new LoadingOverlay();

  /** Сколько операций сейчас держат индикатор. */
  #pending = 0;

  #visible = false;

  readonly #listeners = new Set<() => void>();

  get isVisible(): boolean {
    return this.#visible;
  }

  /** `EasyLoading.show()` */
  show = (): void => {
    this.#pending += 1;
    this.#apply();
  };

  /** `EasyLoading.dismiss()` */
  dismiss = (): void => {
    // Лишний `dismiss()` (в коде их вызывают и «на всякий случай») не должен
    // уводить счётчик в минус — иначе следующий `show()` ничего не покажет.
    this.#pending = Math.max(0, this.#pending - 1);
    this.#apply();
  };

  /** Подписка для `useSyncExternalStore` в `LoadingOverlayHost`. */
  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  getSnapshot = (): boolean => this.#visible;

  #apply(): void {
    const next = this.#pending > 0;
    if (next === this.#visible) return;
    this.#visible = next;
    for (const listener of [...this.#listeners]) {
      listener();
    }
  }
}

export const loadingOverlay = LoadingOverlay.instance;

/**
 * Тот же объект под именем из Dart: страницы и провайдеры переносятся
 * дословно — `EasyLoading.dismiss(); EasyLoading.show();`.
 */
export const EasyLoading = {
  show: loadingOverlay.show,
  dismiss: loadingOverlay.dismiss,
} as const;
